import { useState } from "react";
import { createPortal } from "react-dom";

// Full-screen media viewer (portal, above everything). Tap backdrop or × to close.
export function MediaLightbox({ active, onClose }) {
  if (!active) return null;
  return createPortal(
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 2147483000, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close"
        style={{ position: "fixed", top: "max(14px, env(safe-area-inset-top))", right: 16, zIndex: 1, width: 42, height: 42, borderRadius: "50%", border: "none", cursor: "pointer", background: "rgba(255,255,255,0.16)", color: "#fff", fontSize: 22, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
      {active.kind === "image" && (
        <img src={active.url} alt={active.name || "image"} onClick={(e) => e.stopPropagation()} style={{ maxWidth: "100vw", maxHeight: "100vh", objectFit: "contain", borderRadius: 6 }} />
      )}
      {active.kind === "video" && (
        <video src={active.url} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} style={{ maxWidth: "100vw", maxHeight: "100vh", borderRadius: 6, background: "#000" }} />
      )}
      {active.kind === "audio" && (
        <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--c-bg-card)", padding: 20, borderRadius: 16 }}>
          <audio src={active.url} controls autoPlay style={{ width: "min(80vw, 360px)" }} />
        </div>
      )}
    </div>,
    document.body
  );
}

// Thumbnail for one attachment. `onOpen` opens the full-screen viewer.
export function AttachmentThumb({ a, onOpen }) {
  const thumb = { width: 84, height: 84, objectFit: "cover", borderRadius: 10, border: "1px solid var(--c-border)" };
  if (a.kind === "image") return <img src={a.url} alt={a.name || "image"} onClick={onOpen} style={{ ...thumb, cursor: "pointer" }} />;
  if (a.kind === "video") return (
    <div onClick={onOpen} style={{ position: "relative", width: 84, height: 84, cursor: "pointer" }}>
      <video src={a.url} muted playsInline preload="metadata" style={{ ...thumb, background: "#000" }} />
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 24, textShadow: "0 1px 4px rgba(0,0,0,0.6)", pointerEvents: "none" }}>▶</div>
    </div>
  );
  if (a.kind === "audio") return <audio src={a.url} controls style={{ width: 200, height: 36 }} />;
  return <a href={a.url} target="_blank" rel="noreferrer" style={{ display: "flex", width: 84, height: 84, alignItems: "center", justifyContent: "center", borderRadius: 10, border: "1px solid var(--c-border)", fontSize: 22, textDecoration: "none" }}>📄</a>;
}

// Read-only media preview grid (image/video/audio/file) for detail panels.
// Tapping a photo or video opens it full-screen (lightbox) — handy on mobile.
// `limit` caps how many thumbs show before a "See all" toggle reveals the
// rest — used on reflection cards so a note with a pile of attachments
// doesn't blow out the card's height until the user asks for it.
export function AttachmentViewer({ items, limit }) {
  const list = Array.isArray(items) ? items : [];
  const [active, setActive] = useState(null);
  const [showAll, setShowAll] = useState(false);
  if (!list.length) return null;
  const capped = limit && !showAll && list.length > limit;
  const visible = capped ? list.slice(0, limit) : list;
  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {visible.map((a) => (
          <div key={a.id} style={{ width: a.kind === "audio" ? 200 : 84 }}>
            <AttachmentThumb a={a} onOpen={() => setActive(a)} />
          </div>
        ))}
      </div>
      {limit && list.length > limit && (
        <button onClick={e => { e.stopPropagation(); setShowAll(v => !v); }}
          style={{ marginTop: 6, background: "none", border: "none", padding: 0, fontSize: 11, fontWeight: 800, color: "var(--c-text-accent)", cursor: "pointer" }}>
          {showAll ? "Show fewer attachments" : `See all attachments (${list.length})`}
        </button>
      )}
      <MediaLightbox active={active} onClose={() => setActive(null)} />
    </>
  );
}
